import React, { useEffect, useState } from "react";
import { useParams, Navigate, useLocation } from "react-router-dom";
import { pagesApi, type Page } from "@/services/pagesApi";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

const DynamicPage: React.FC = () => {
  const location = useLocation();
  // Extract slug from the current pathname
  const actualSlug = location.pathname.startsWith('/') ? location.pathname.substring(1) : location.pathname;
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPage = async () => {
      console.log('DynamicPage - pathname:', location.pathname, 'actualSlug:', actualSlug);
      if (!actualSlug) return;
      
      try {
        setLoading(true);
        setError(null);
        const pageData = await pagesApi.getPageBySlug(actualSlug);
        setPage(pageData);
        
        // Set page title and meta description
        document.title = `${pageData.name} | Mon Copieur`;
        let metaTag = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
        if (!metaTag) {
          metaTag = document.createElement("meta");
          metaTag.name = "description";
          document.head.appendChild(metaTag);
        }
        // Extract text from HTML for description
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = pageData.content_html;
        const textContent = tempDiv.textContent || tempDiv.innerText || '';
        metaTag.content = textContent.substring(0, 160);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
        
        // If page not found, we'll handle it with Navigate component
        if (errorMessage === 'Page not found') {
          return;
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [actualSlug]);

  if (!actualSlug) {
    return <Navigate to="/404" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="border-b border-border bg-secondary/40">
          <div className="container mx-auto px-4 py-10">
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-96" />
          </div>
        </header>
        <article className="container mx-auto px-4 py-8">
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </article>
      </div>
    );
  }

  if (error === 'Page not found') {
    return <Navigate to="/404" replace />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <div className="container mx-auto px-4 py-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error === 'Page is not published' 
                ? 'Cette page n\'est pas encore disponible.' 
                : 'Une erreur est survenue lors du chargement de la page.'
              }
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (!page) {
    return <Navigate to="/404" replace />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-secondary/40">
        <div className="container mx-auto px-4 py-10">
          <h1 className="text-3xl font-semibold">{page.name}</h1>
        </div>
      </header>

      <article className="prose prose-neutral max-w-none container mx-auto px-4 py-8">
        <div 
          dangerouslySetInnerHTML={{ __html: page.content_html }}
          className="dynamic-content"
        />
        <div className="mt-8 text-sm text-muted-foreground">
          Dernière mise à jour : {new Date(page.last_update_date).toLocaleDateString('fr-FR')}
        </div>
      </article>
    </div>
  );
};

export default DynamicPage;